import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  IAction,
  IScreenDesignInfo,
  IScreenInput,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

export interface INavigationScreenInputPickerState {}

export interface INavigationScreenInputPickerProps {
  action?: IAction;
  targetScreenId: string;
  targetScreenInputParams: ITargetScreenInputParam[];
  updateTargetScreenInputParams: (params: ITargetScreenInputParam[], changeReason?: ESdChangeReason) => void;
  deleteTargetScreenInputParams: (parameterName: string) => void;
  screenType?: string;
  screenList?: IScreenDesignInfo[];
}

/**
 * Test-id base for the picker that supplies a LIVE target-screen input, derived from the input name
 * the same way `<parameterName>SymbolPicker` already is elsewhere in the panel.
 */
const navigationScreenInputTestId = (inputName: string): string => `navigationScreenInput${inputName}`;

/** Test-id base for a parameter the target screen no longer declares (renamed or deleted there). */
const deprecatedNavigationScreenInputTestId = (inputName: string): string =>
  `navigationDeprecatedScreenInput${inputName}`;

class NavigationScreenInputPicker extends PureSdComponent<
  INavigationScreenInputPickerProps & IWithSDProps & IWithContextProp,
  INavigationScreenInputPickerState
> {
  constructor(props: INavigationScreenInputPickerProps & IWithSDProps & IWithContextProp) {
    super(props);
  }

  handleScreenInputChange = (
    name: string,
    type: string,
    cardinality: ECardinality,
    symbol: ISymbol,
    changeReason?: ESdChangeReason
  ) => {
    const { targetScreenInputParams } = this.props;
    const targetScreenInputParam = targetScreenInputParams.find((t) => t.name === name);
    if (targetScreenInputParam) {
      targetScreenInputParam.symbol = symbol;
    } else {
      const newScreenInputParam: ITargetScreenInputParam = {
        cardinality,
        name,
        symbol,
        type,
        typeName: type
      };
      targetScreenInputParams.push(newScreenInputParam);
    }
    this.props.updateTargetScreenInputParams(targetScreenInputParams, changeReason);
  };

  /**
   * One target-screen-input row: its `ActionParameterTitle` caption (name, then a type chip, then a
   * cardinality chip when it is not One) plus the picker that supplies the value.
   *
   * `parameterName` and `label` are separate on purpose. The caption of a deprecated row carries a
   * "- (Deprecated)" suffix, and the identity written back through `handleScreenInputChange` must
   * stay the input's real name — otherwise editing a deprecated row's value appends a THIRD
   * parameter called "<name>- (Deprecated)" instead of updating the one on screen.
   *
   * `testIdBase` gives each row its own handles (`<base>Label`, `<base>SymbolPicker`,
   * `<base>FixedValueInput`, `<base>Switch`, `<base>BoundValue`). These pickers carry neither an
   * action parameter nor a component property, so without it every row on a Navigate action renders
   * the same anonymous ids and no test can address one input among several (QA-423).
   */
  renderParameter = (
    symbol: ISymbol | undefined,
    parameterName: string,
    label: string,
    type: string,
    cardinality: ECardinality,
    testIdBase: string
  ) => {
    return (
      <div key={Guid.create().toString()} data-testid="navigationScreenInputParam">
        <span
          style={{ marginBottom: 6, marginTop: 8 }}
          className="k_Regular-G1-Left kuika_rightPanelDIName"
          data-testid={`${testIdBase}Label`}
        >
          <ActionParameterTitle parameter={{ name: label, Typename: type, cardinality }} />
        </span>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          key={Guid.create().toString()}
          symbol={symbol}
          valueType={type ? (type as BackEndValueType) : (type as BackEndValueType)}
          targetCardinality={cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleScreenInputChange(parameterName, type, cardinality, symbol, changeReason)
          }
          data_testid={testIdBase}
        />
      </div>
    );
  };

  render() {
    if (this.props.targetScreenId === undefined || this.props.targetScreenId.length === 0) {
      return <></>;
    }

    const targetScreenInputDefinitionList = this.props.sd.getScreenInputsByScreenId(
      this.props.targetScreenId,
      this.props.screenList
    );
    const targetMasterScreenInputDefinitionList = this.props.sd.getMasterScreenInputsByScreenId(
      this.props.targetScreenId
    );

    const allScreenInputList = [...targetScreenInputDefinitionList, ...targetMasterScreenInputDefinitionList];

    return (
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        {this.screenInputForm(targetScreenInputDefinitionList, `${this.props.screenType ?? "Screen"} Inputs:`)}
        {targetMasterScreenInputDefinitionList &&
          targetMasterScreenInputDefinitionList.length > 0 &&
          this.screenInputForm(targetMasterScreenInputDefinitionList, "Master Screen Inputs:")}
        {this.deprecatedScreenInputList(allScreenInputList)}
      </div>
    );
  }

  deprecatedScreenInputList = (allScreenInputList: IScreenInput[]) => {
    return (
      <>
        {this.props.targetScreenInputParams?.map((targetScreenInput) => {
          const findedTargetParam = allScreenInputList?.find((p) => p.name === targetScreenInput.name);
          return (
            findedTargetParam === undefined && (
              <>
                <div
                  className="isDeletedOnSource"
                  data-testid={`${deprecatedNavigationScreenInputTestId(targetScreenInput.name)}Row`}
                >
                  {this.renderParameter(
                    targetScreenInput.symbol,
                    targetScreenInput.name,
                    `${targetScreenInput.name}- (Deprecated)`,
                    targetScreenInput.type,
                    targetScreenInput.cardinality,
                    deprecatedNavigationScreenInputTestId(targetScreenInput.name)
                  )}
                  <div className="kuika_styling_typography_footer" style={{ padding: "4px 6px", display: "flow-root" }}>
                    <div
                      style={{ float: "right", color: "var(--red-6)" }}
                      data-testid={`${deprecatedNavigationScreenInputTestId(targetScreenInput.name)}RemoveButton`}
                      onClick={() => {
                        if (this.props.deleteTargetScreenInputParams) {
                          this.props.deleteTargetScreenInputParams(targetScreenInput.name);
                        }
                      }}
                    >
                      Remove
                    </div>
                  </div>
                </div>
              </>
            )
          );
        })}
      </>
    );
  };

  screenInputForm = (targetScreenInputDefinitionList: IScreenInput[], title: string) => {
    {
      if (
        (!targetScreenInputDefinitionList || targetScreenInputDefinitionList.length === 0) &&
        (!this.props.targetScreenInputParams || this.props.targetScreenInputParams.length === 0)
      ) {
        return <></>;
      }
      return (
        <>
          <span className="kuika_navigation_screen_input_title">{title}</span>
          {targetScreenInputDefinitionList?.map((targetScreenInputDefinition) => {
            const param = this.props.targetScreenInputParams.find(
              (screenInput) => screenInput.name === targetScreenInputDefinition.name
            );
            return (
              <>
                {this.renderParameter(
                  param?.symbol,
                  targetScreenInputDefinition.name,
                  targetScreenInputDefinition.name,
                  targetScreenInputDefinition.type,
                  targetScreenInputDefinition.cardinality,
                  navigationScreenInputTestId(targetScreenInputDefinition.name)
                )}
              </>
            );
          })}
        </>
      );
    }
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const navigationScreenInputPicker = withContext<INavigationScreenInputPickerProps>(
  withSD<INavigationScreenInputPickerProps>(NavigationScreenInputPicker)
);

export { navigationScreenInputPicker as NavigationScreenInputPicker };
